import React from 'react';
import { useLanguage } from '../../i18n/LanguageContext';

export type LoginGatewayStep = 'authenticating' | 'connecting' | 'preparing' | 'welcome' | 'redirecting';

interface LoginGatewayLoaderProps {
  step: LoginGatewayStep;
  progress: number;
  email?: string;
  role?: string;
  tenantSlug?: string;
  userName?: string;
}

export const LoginGatewayLoader: React.FC<LoginGatewayLoaderProps> = ({
  step,
  progress,
  email,
  role,
  tenantSlug,
  userName,
}) => {
  const { t } = useLanguage();

  // Determine display name
  const displayName = userName || (email ? email.split('@')[0] : 'Learner');
  const formattedRole = role
    ? (role === 'LEARNER'
        ? t('auth.role_learner', undefined, 'Learner')
        : role === 'TENANT_ADMIN'
        ? t('auth.role_admin', undefined, 'Admin')
        : role === 'PLATFORM_SUPER_ADMIN'
        ? t('auth.role_super_admin', undefined, 'Super Admin')
        : role.replace(/_/g, ' ')
      ).toUpperCase()
    : 'LEARNER PORTAL';

  // Step descriptions and statuses
  const stepsConfig = [
    {
      id: 'authenticating',
      title: t('gateway.security_auth_title', undefined, 'Security & Auth'),
      sub: t('gateway.security_auth_sub', undefined, 'Verifying session tokens'),
      icon: '🔒',
      threshold: 25,
    },
    {
      id: 'connecting',
      title: t('gateway.tenant_gateway_title', undefined, 'Tenant Gateway'),
      sub: tenantSlug
        ? t('gateway.tenant_gateway_sub_workspace', { tenant: tenantSlug }, `${tenantSlug} workspace`)
        : t('gateway.tenant_gateway_sub_default', undefined, 'Enterprise tenant'),
      icon: '🏢',
      threshold: 50,
    },
    {
      id: 'preparing',
      title: t('gateway.ai_core_title', undefined, 'AI Persona Core'),
      sub: t('gateway.ai_core_sub', undefined, 'Syncing simulations & models'),
      icon: '⚡',
      threshold: 75,
    },
    {
      id: 'welcome',
      title: t('gateway.ready_title', undefined, 'Ready'),
      sub: t('gateway.ready_sub', undefined, 'Launching portal'),
      icon: '✨',
      threshold: 100,
    },
  ];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        background: 'radial-gradient(circle at 50% 40%, #0F2B5C 0%, #071D45 60%, #040D21 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        animation: 'gatewayFadeIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards',
        overflow: 'hidden',
        userSelect: 'none',
      }}
    >
      <style>{`
        @keyframes gatewayFadeIn {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes orbitRotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes orbitRotateRev {
          0% { transform: rotate(360deg); }
          100% { transform: rotate(0deg); }
        }
        @keyframes pulseGlowRing {
          0%, 100% { transform: scale(1); opacity: 0.55; }
          50% { transform: scale(1.12); opacity: 0.9; }
        }
        @keyframes cardPopSpring {
          0% { opacity: 0; transform: translateY(16px) scale(0.96); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes shimmerGradient {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes floatIcon {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
      `}</style>

      {/* Ambient background particles/glows */}
      <div
        style={{
          position: 'absolute',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(47, 123, 255, 0.22) 0%, rgba(13, 79, 189, 0.05) 60%, transparent 80%)',
          filter: 'blur(40px)',
          animation: 'pulseGlowRing 4s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />

      {/* Main Glassmorphism Card */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '460px',
          background: 'rgba(255, 255, 255, 0.06)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid rgba(255, 255, 255, 0.14)',
          borderRadius: '24px',
          boxShadow: '0 32px 80px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.08) inset',
          padding: '36px 28px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          animation: 'cardPopSpring 0.45s cubic-bezier(0.16, 1, 0.3, 1) forwards',
        }}
      >
        {/* Animated AI Orbital Core */}
        <div
          style={{
            position: 'relative',
            width: '104px',
            height: '104px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Outer glowing orbit */}
          <div
            style={{
              position: 'absolute',
              inset: '-6px',
              borderRadius: '50%',
              border: '2px dashed rgba(96, 165, 250, 0.4)',
              animation: 'orbitRotate 8s linear infinite',
            }}
          />

          {/* Inner reverse orbit */}
          <div
            style={{
              position: 'absolute',
              inset: '2px',
              borderRadius: '50%',
              border: '2px solid transparent',
              borderTopColor: '#60A5FA',
              borderRightColor: '#A855F7',
              animation: 'orbitRotateRev 3s linear infinite',
            }}
          />

          {/* Central Core Sphere */}
          <div
            style={{
              width: '76px',
              height: '76px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 50%, #60A5FA 100%)',
              boxShadow: '0 0 32px rgba(59, 130, 246, 0.6), 0 0 12px rgba(255, 255, 255, 0.4) inset',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'pulseGlowRing 2.5s ease-in-out infinite',
            }}
          >
            <span
              style={{
                fontSize: '32px',
                filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))',
                animation: 'floatIcon 2s ease-in-out infinite',
              }}
            >
              {step === 'welcome' || step === 'redirecting' ? '🚀' : '✨'}
            </span>
          </div>
        </div>

        {/* Brand & Dynamic Title */}
        <div style={{ marginBottom: '8px' }}>
          <div
            style={{
              fontFamily: 'var(--display, "Lexend", sans-serif)',
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
              color: '#93C5FD',
              marginBottom: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            <span>TRAINOVA.AI</span>
            <span style={{ opacity: 0.5 }}>•</span>
            <span>ENTERPRISE PORTAL</span>
          </div>

          {step === 'welcome' || step === 'redirecting' ? (
            <h2
              style={{
                fontFamily: 'var(--display, "Lexend", sans-serif)',
                fontSize: '26px',
                fontWeight: 700,
                color: '#FFFFFF',
                margin: 0,
                lineHeight: 1.25,
                letterSpacing: '-0.02em',
                background: 'linear-gradient(135deg, #FFFFFF 0%, #BFDBFE 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('gateway.welcome_back', { name: displayName }, `Welcome back, ${displayName}!`)}
            </h2>
          ) : (
            <h2
              style={{
                fontFamily: 'var(--display, "Lexend", sans-serif)',
                fontSize: '24px',
                fontWeight: 700,
                color: '#FFFFFF',
                margin: 0,
                lineHeight: 1.25,
                letterSpacing: '-0.02em',
              }}
            >
              {t('gateway.entering_portal', undefined, 'Entering Portal…')}
            </h2>
          )}
        </div>

        {/* Role & Workspace Badge */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '5px 14px',
            borderRadius: '999px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            fontSize: '12px',
            fontWeight: 600,
            color: '#E0E7FF',
            marginBottom: '24px',
          }}
        >
          <span>{role?.includes('ADMIN') ? '🛡️' : '🎓'}</span>
          <span>{formattedRole}</span>
          {tenantSlug && (
            <>
              <span style={{ opacity: 0.4 }}>|</span>
              <span style={{ color: '#93C5FD' }}>{tenantSlug}</span>
            </>
          )}
        </div>

        {/* Progress Bar Container */}
        <div style={{ width: '100%', marginBottom: '24px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '12.5px',
              fontWeight: 500,
              color: '#94A3B8',
              marginBottom: '8px',
            }}
          >
            <span>
              {step === 'authenticating' && t('gateway.status_authenticating', undefined, 'Authenticating session…')}
              {step === 'connecting' && t('gateway.status_connecting', undefined, 'Connecting to workspace…')}
              {step === 'preparing' && t('gateway.status_preparing', undefined, 'Loading AI simulation engines…')}
              {(step === 'welcome' || step === 'redirecting') && t('gateway.status_ready', undefined, 'Ready! Launching dashboard…')}
            </span>
            <span style={{ color: '#60A5FA', fontWeight: 700 }}>{Math.round(progress)}%</span>
          </div>

          <div
            style={{
              width: '100%',
              height: '8px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '999px',
              overflow: 'hidden',
              position: 'relative',
              boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.3)',
            }}
          >
            <div
              style={{
                width: `${Math.min(100, Math.max(8, progress))}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #2563EB, #60A5FA, #A855F7, #60A5FA)',
                backgroundSize: '200% 100%',
                animation: 'shimmerGradient 2s linear infinite',
                borderRadius: '999px',
                transition: 'width 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 0 12px rgba(96, 165, 250, 0.8)',
              }}
            />
          </div>
        </div>

        {/* 4 Multi-Step Checkpoint Grid */}
        <div
          style={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '8px',
            paddingTop: '16px',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          {stepsConfig.map((item) => {
            const isCompleted = progress >= item.threshold;
            const isCurrent =
              progress >= item.threshold - 25 && progress < item.threshold;

            return (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '13px',
                    background: isCompleted
                      ? '#10B981'
                      : isCurrent
                      ? 'rgba(59, 130, 246, 0.35)'
                      : 'rgba(255, 255, 255, 0.06)',
                    border: isCompleted
                      ? '1px solid #34D399'
                      : isCurrent
                      ? '1px solid #60A5FA'
                      : '1px solid rgba(255, 255, 255, 0.1)',
                    color: isCompleted ? '#FFFFFF' : '#94A3B8',
                    boxShadow: isCompleted
                      ? '0 0 10px rgba(16, 185, 129, 0.5)'
                      : isCurrent
                      ? '0 0 10px rgba(59, 130, 246, 0.4)'
                      : 'none',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {isCompleted ? '✓' : item.icon}
                </div>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: isCompleted || isCurrent ? 600 : 400,
                    color: isCompleted
                      ? '#FFFFFF'
                      : isCurrent
                      ? '#93C5FD'
                      : '#64748B',
                    lineHeight: 1.1,
                  }}
                >
                  {item.title}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
